import { useState, useEffect } from 'react';
import axios from 'axios';
import { calculateCurrentFiscalYear, type TrialBalanceRow } from '../components/profitAndLoss/constants';
import { ep } from '../api/endpoints';

/**
 * 月次合計試算表ページのデータ層フック。
 *
 * 指定した会計年度（9〜翌8月）の PL（損益）科目を、月次推移マトリクス形式
 * （各月 month_{m}_actual = その月の発生額）で取得する。
 * 既存の損益マトリクス API get_obc_trial_balance をそのまま再利用し、
 * 予算・前年は使わず当年度実績のみを月別推移として表示する。
 *
 * 年度・拠点・部署の絞り込みと、ツリービュー（科目区分・勘定科目）の開閉を提供する。
 * balance（=month_{m}_actual）は控除科目について同期時に符号反転済みである点に注意。
 */
export function useMonthlyTrialBalance() {
  const [rows, setRows] = useState<TrialBalanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 会計年度 (9月開始・8月決算)
  const [fiscalYear, setFiscalYear] = useState(calculateCurrentFiscalYear());
  // 拠点絞り込み (branch_code 配列。空 = 全拠点)
  const [selectedBranchCodes, setSelectedBranchCodes] = useState<string[]>([]);
  // 部署絞り込み (department_code 配列。空 = 全部署)
  const [selectedDepartmentCodes, setSelectedDepartmentCodes] = useState<string[]>([]);

  // 🔽 ツリービュー（科目区分セクションの開閉）。初期状態は全セクション閉じる
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  // 🔽 ツリービュー（勘定科目の開閉 = 補助元帳の展開）
  const [expandedAccounts, setExpandedAccounts] = useState<Record<string, boolean>>({});
  const toggleAccount = (accountCode: string) => {
    setExpandedAccounts(prev => ({ ...prev, [accountCode]: !prev[accountCode] }));
  };

  /**
   * 🚀 年度の月次推移マトリクスを取得（予算バージョン指定なし = 当年度実績のみ）
   */
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    // 再フェッチ中は前回データを保持してちらつきを防ぐ（空/エラー時のみクリア）

    try {
      const response = await axios.post(
        ep('get_obc_trial_balance'),
        {
          year: fiscalYear,
          mode: 'matrix',
          budget_version_code: null,
          branch_codes: selectedBranchCodes.length > 0 ? selectedBranchCodes : null,
          department_codes: selectedDepartmentCodes.length > 0 ? selectedDepartmentCodes : null,
        }
      );

      if (response.data.status === 'error') {
        throw new Error(response.data.message || response.data.error || 'API側でエラーが発生しました');
      }

      const extractedData: TrialBalanceRow[] = response.data.data || [];

      if (extractedData.length === 0) {
        setRows([]);
        setError(`${fiscalYear}年度のデータがDBに見つかりませんでした。同期状況を確認してください。`);
      } else {
        setRows(extractedData);
      }
    } catch (err: any) {
      console.error(err);
      setRows([]);
      setError(`通信エラー: ${err.message || '不明なエラー'}`);
    } finally {
      setLoading(false);
    }
  };

  // データ取得（年度・拠点・部署変更時）
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fiscalYear, selectedBranchCodes, selectedDepartmentCodes]);

  return {
    rows,
    loading,
    error,
    // 絞り込み state
    fiscalYear,
    setFiscalYear,
    selectedBranchCodes,
    setSelectedBranchCodes,
    selectedDepartmentCodes,
    setSelectedDepartmentCodes,
    // ツリービュー開閉
    expandedSections,
    toggleSection,
    expandedAccounts,
    toggleAccount,
  };
}

export type UseMonthlyTrialBalanceResult = ReturnType<typeof useMonthlyTrialBalance>;
